import React, { FC, ReactNode, useState, useEffect } from "react";
import NextLink from "next/link";
import { useApolloClient } from "@apollo/client";

import Tooltip from "@mui/material/Tooltip";
import SchemaDescription from "./SchemaDescription";
import {
  PropertyDescriptionQuery,
  PropertyDescriptionQueryVariables,
  SchemaReference,
  SubjectDescriptionQuery,
  SubjectDescriptionQueryVariables,
} from "src/lib/graphql/types";
import {
  propertyDescriptionQuery,
  subjectDescriptionQuery,
} from "src/lib/graphql/schemas.queries";

const SchemaLink: FC<{
  href?: string;
  name?: string;
  namespace?: string;
  end?: ReactNode;
  tooltip?: boolean;
}> = ({
  href = "",
  name,
  namespace,
  children,
  end = null,
  tooltip = false,
}) => {
  const apolloClient = useApolloClient();

  const [description, setDescription] = useState<string | undefined>();

  const fetchDescription = ({ name, namespace }: SchemaReference) =>
    apolloClient
      .query<
        PropertyDescriptionQuery | SubjectDescriptionQuery,
        PropertyDescriptionQueryVariables | SubjectDescriptionQueryVariables
      >({
        query:
          name[0] === name[0].toUpperCase()
            ? subjectDescriptionQuery
            : propertyDescriptionQuery,
        variables: { name, namespace: namespace ?? "schema" },
      })
      .then((res) => {
        if ("property" in res.data) {
          return res.data.property.description;
        }
        return res.data.subject.description;
      })
      .catch(() => undefined);

  const isExternal = href.startsWith("http");
  const linkProps = isExternal
    ? { href, target: "_blank", rel: "noopener noreferrer nofollow" }
    : {};

  useEffect(() => {
    if (!isExternal && tooltip && name && namespace)
      fetchDescription({ name, namespace }).then((desc) =>
        setDescription(desc)
      );
  }, [isExternal, name, namespace, tooltip]);

  const linkContents = (
    <a
      {...linkProps}
      title={description ? undefined : name}
      className="schema-link no-underline"
    >
      <span className="schema-link-underline">{children}</span>
      {isExternal ? (
        <>
          &nbsp;
          <img src="/assets/open-in-new.svg" className="external-link-icon" />
        </>
      ) : null}
      {end}
    </a>
  );

  const contents = isExternal ? (
    linkContents
  ) : (
    <NextLink href={href}>{linkContents}</NextLink>
  );

  return description ? (
    <Tooltip
      placement="bottom-start"
      arrow
      title={<SchemaDescription description={description} />}
    >
      <span>{contents}</span>
    </Tooltip>
  ) : (
    contents
  );
};

export default SchemaLink;
